import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

// components
import HookForm from '../../components/HookForm/HookForm';
import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';
import SelectStatuses from '../../components/Selects/SelectStatuses';
import SelectManager from '../../components/Selects/SelectManager';
import LeadCarCard from './LeadCarCard';

// icons
import phoneIcon from '../../brand/generated/input/phone.svg';

// api
import getLeadCard from '../../api/leads/getLeadCard';
import editLead from '../../api/leads/editLead';
import acceptLead from '../../api/leads/acceptLead';
import { ROLE_MANAGER } from '../../constants/roles';
import addLeadComment from '../../api/leads/addLeadComment';
import checkCrmSidebarNotifications from '../../api/notifications/checkCrmSidebarNotifications';
import alert from '../../helpers/alert';
import formatDate from '../../utils/formatDate';
import fixDateTimezone from '../../utils/fixDateTimezone';

const formStyles = {
  leadInfo: { display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(4, 1fr)' },
};

const styles = {
  card: { background: '#fff', border: '1px solid #E7E7ED', borderRadius: 8, padding: 24, marginTop: 20 },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  cardTitle: { fontWeight: 600, fontSize: 15 },
  counter: {
    fontSize: 11,
    fontWeight: 600,
    color: '#8B8B8B',
    background: '#F4F4F6',
    borderRadius: 10,
    padding: '2px 8px',
  },
  commentList: { display: 'grid', gap: 12, marginBottom: 20 },
  comment: { borderLeft: '2px solid var(--brand-primary)', background: '#FAFAFB', borderRadius: '0 4px 4px 0', padding: '10px 14px' },
  commentMeta: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 },
  commentAuthor: { fontSize: 12, fontWeight: 600, color: '#2E3135' },
  commentDate: { fontSize: 11, color: '#8B8B8B' },
  commentText: { fontSize: 13, lineHeight: '20px', color: '#2E3135', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  empty: { color: '#8B8B8B', fontSize: 13, marginBottom: 20 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'stretch' },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E7E7ED',
    borderRadius: 4,
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: '20px',
    color: '#2E3135',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  formActions: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
  hint: { fontSize: 10, color: '#B4B4B4' },
  acceptBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: '#FAFAFB',
    border: '1px solid #E7E7ED',
    borderRadius: 8,
    padding: '12px 24px',
    marginTop: 20,
    marginBottom: 20,
  },
};

const asOption = (id, label) => (id ? { value: id, label } : undefined);

const LeadMainInfo = () => {
  const { id } = useParams();
  const [lead, setLead] = useState();
  const [loading, setLoading] = useState(true);
  // ÐÐ°ÑÑÐ¾ÑÐºÐ° Ð¾ÑÐºÑÑÐ²Ð°ÐµÑÑÑ Ð² ÑÐµÐ¶Ð¸Ð¼Ðµ Ð¿ÑÐ¾ÑÐ¼Ð¾ÑÑÐ°; ÐºÐ°ÑÐ°Ð½Ð´Ð°Ñ Ñ Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐ° Ð²ÐºÐ»ÑÑÐ°ÐµÑ Ð¿ÑÐ°Ð²ÐºÑ â
  // ÑÐ°Ð½ÑÑÐµ Ð¾Ð½ Ð½Ðµ Ð´ÐµÐ»Ð°Ð» Ð½Ð¸ÑÐµÐ³Ð¾, Ð¸ Ð¿Ð¾Ð¼ÐµÐ½ÑÑÑ ÑÑÐ¾-Ð»Ð¸Ð±Ð¾ Ð¸Ð· ÐºÐ°ÑÑÐ¾ÑÐºÐ¸ Ð±ÑÐ»Ð¾ Ð½ÐµÐ»ÑÐ·Ñ.
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  // Ð ÑÐ´Ð¾Ð²Ð¾Ð¹ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð»Ð¸Ð´Ñ Ð½Ðµ ÑÐ°Ð·Ð´Ð°ÑÑ â Ð¾ÑÐ²ÐµÑÑÑÐ²ÐµÐ½Ð½Ð¾Ð³Ð¾ ÐµÐ¼Ñ ÑÑÐ°Ð²Ð¸Ñ ÑÐ¾Ð»ÑÐºÐ¾ ÐºÐ½Ð¾Ð¿ÐºÐ° Â«ÐÑÐ¸Ð½ÑÑÑ Ð² ÑÐ°Ð±Ð¾ÑÑÂ».
  const isManager = useSelector((state) => state?.user?.roleId) === ROLE_MANAGER;

  const load = () => {
    return getLeadCard({ id })
      .then((res) => {
        setLead(res.data);
        if (res.data?.notifications?.length > 0) {
          checkCrmSidebarNotifications({
            notificationIds: res.data.notifications.map((item) => item.id),
          }).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    load();
  }, [id]);

  const handleSubmit = (values) => {
    // Ð¨Ð»ÑÐ¼ ÑÐ¾Ð»ÑÐºÐ¾ Ð¸Ð·Ð¼ÐµÐ½ÑÐ½Ð½ÑÐµ Ð¿Ð¾Ð»Ñ. editLead Ð¿ÑÐ¸Ð¼ÐµÐ½ÑÐµÑ Ð²ÑÑ, ÑÑÐ¾ Ð¿ÑÐ¸ÑÐ»Ð¾ Ð½Ðµ-null, Ð° Ð½Ð°
    // statusId=9 Ð·Ð°Ð²Ð¾Ð´Ð¸Ñ ÑÐ´ÐµÐ»ÐºÑ â Ð»Ð¸ÑÐ½Ð¸Ð¹ ÑÐ°Ð· ÑÑÐ¾Ñ ÑÑÐ°ÑÑÑ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑ Ð½Ðµ Ð½Ð°Ð´Ð¾.
    const payload = { id: Number(id) };
    if (values.fio !== lead?.fio) payload.fio = values.fio;
    if (values.phone !== lead?.phone) payload.phone = values.phone;
    if (values.statusId?.value && values.statusId.value !== lead?.status?.id)
      payload.statusId = values.statusId.value;
    if (values.managerId?.value && values.managerId.value !== lead?.manager?.id)
      payload.managerId = values.managerId.value;

    if (Object.keys(payload).length === 1) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    editLead(payload)
      .then(() => {
        alert.success('ÐÐ¸Ð´ Ð¾Ð±Ð½Ð¾Ð²Ð»ÑÐ½');
        setEditMode(false);
        return load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleAccept = () => {
    setSaving(true);
    acceptLead(Number(id))
      .then(() => {
        alert.success('ÐÐ¸Ð´ Ð¿ÑÐ¸Ð½ÑÑ Ð² ÑÐ°Ð±Ð¾ÑÑ');
        window.dispatchEvent(new Event('crmSidebarRefresh'));
      })
      .catch(() => {})
      // Ð Ð¿ÑÐ¸ Ð¾ÑÐºÐ°Ð·Ðµ (Â«ÑÐ¶Ðµ Ð²Ð·ÑÑ Ð´ÑÑÐ³Ð¸Ð¼Â») ÐºÐ°ÑÑÐ¾ÑÐºÑ Ð¿ÐµÑÐµÑÐ¸ÑÑÐ²Ð°ÐµÐ¼ â Ð¾Ð½Ð° ÑÑÑÐ°ÑÐµÐ»Ð°.
      .then(load)
      .finally(() => setSaving(false));
  };

  const handleAddComment = () => {
    const text = comment.trim();
    if (!text) return;
    setSaving(true);
    addLeadComment({ leadId: Number(id), comment: text })
      .then(() => {
        setComment('');
        return load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  if (loading) return <Loader />;

  const inputs = [
    {
      name: 'createdAt',
      label: 'ÐÐ°ÑÐ°',
      defaultValue: lead?.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '',
      disabled: true,
    },
    { name: 'fio', label: 'ÐÐ¼Ñ', defaultValue: lead?.fio || '', disabled: !editMode },
    {
      name: 'source',
      label: 'ÐÑÑÐ¾ÑÐ½Ð¸Ðº',
      defaultValue: lead?.source?.title || '',
      disabled: true,
    },
    {
      name: 'phone',
      label: 'Ð¢ÐµÐ»ÐµÑÐ¾Ð½',
      icon: phoneIcon,
      defaultValue: lead?.phone || '',
      disabled: !editMode,
    },
    { name: 'mark', label: 'ÐÐ°ÑÐºÐ° Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ', defaultValue: lead?.mark?.name || '', disabled: true },
    { name: 'model', label: 'ÐÐ¾Ð´ÐµÐ»Ñ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ', defaultValue: lead?.model?.name || '', disabled: true },
    {
      name: 'statusId',
      component: SelectStatuses,
      label: 'Ð¡ÑÐ°ÑÑÑ',
      defaultValue: asOption(lead?.status?.id, lead?.status?.title),
      disabled: !editMode,
    },
    {
      name: 'managerId',
      component: SelectManager,
      label: 'ÐÐµÐ½ÐµÐ´Ð¶ÐµÑ',
      defaultValue: asOption(lead?.manager?.id, lead?.manager?.fullName),
      disabled: !editMode || isManager,
    },
  ];

  return (
    <>
      {!lead?.manager && (
        <div style={styles.acceptBar}>
          <span>Ð£ Ð»Ð¸Ð´Ð° Ð¿Ð¾ÐºÐ° Ð½ÐµÑ Ð¾ÑÐ²ÐµÑÑÑÐ²ÐµÐ½Ð½Ð¾Ð³Ð¾ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÐ°</span>
          <Button text="ÐÑÐ¸Ð½ÑÑÑ Ð² ÑÐ°Ð±Ð¾ÑÑ" fill disabled={saving} onClick={handleAccept} />
        </div>
      )}
      <HookForm
        // key â ÑÑÐ¾Ð±Ñ Ð¿ÑÐ¸ Ð²ÑÑÐ¾Ð´Ðµ Ð¸Ð· Ð¿ÑÐ°Ð²ÐºÐ¸ Ð¿Ð¾Ð»Ñ Ð¿ÐµÑÐµÑÐ¸ÑÐ°Ð»Ð¸ÑÑ Ð¸Ð· ÑÐ²ÐµÐ¶ÐµÐ³Ð¾ Ð»Ð¸Ð´Ð°
        key={`lead-${id}-${editMode}-${lead?.status?.id}-${lead?.manager?.id}`}
        inputs={inputs}
        title={'ÐÐ½ÑÐ¾ÑÐ¼Ð°ÑÐ¸Ñ Ð¾ Ð»Ð¸Ð´Ðµ'}
        grid={formStyles.leadInfo}
        border
        edit
        handleFormEdit={() => setEditMode(!editMode)}
        action={editMode ? 'Ð¡Ð¾ÑÑÐ°Ð½Ð¸ÑÑ' : undefined}
        loading={saving}
        onSubmit={handleSubmit}
      />

      <LeadCarCard lead={lead} onChanged={load} />

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <p style={styles.cardTitle}>ÐÐ°Ð¼ÐµÑÐºÐ¸ Ð¾ Ð»Ð¸Ð´Ðµ</p>
          {lead?.comments?.length > 0 && <span style={styles.counter}>{lead.comments.length}</span>}
        </div>

        {lead?.comments?.length > 0 ? (
          <div style={styles.commentList}>
            {lead.comments.map((item) => (
              <div key={item.id} style={styles.comment}>
                <div style={styles.commentMeta}>
                  <span style={styles.commentAuthor}>{item.author || 'ÐÐ²ÑÐ¾Ñ Ð½Ðµ ÑÐºÐ°Ð·Ð°Ð½'}</span>
                  {item.createdAt && (
                    <span style={styles.commentDate}>{formatDate(fixDateTimezone(item.createdAt), true)}</span>
                  )}
                </div>
                <p style={styles.commentText}>{item.comment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={styles.empty}>ÐÐ°Ð¼ÐµÑÐ¾Ðº Ð¿Ð¾ÐºÐ° Ð½ÐµÑ â Ð¿ÐµÑÐ²Ð°Ñ Ð¿Ð¾ÑÐ²Ð¸ÑÑÑ Ð·Ð´ÐµÑÑ.</p>
        )}

        <div style={styles.formRow}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter â Ð¿ÑÐ¸Ð²ÑÑÐ½Ð°Ñ Ð¾ÑÐ¿ÑÐ°Ð²ÐºÐ°, ÑÑÐ¾Ð±Ñ Ð½Ðµ ÑÑÐ½ÑÑÑÑÑ Ð¼ÑÑÑÑ Ðº ÐºÐ½Ð¾Ð¿ÐºÐµ
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAddComment();
            }}
            placeholder="ÐÑÑÐ°Ð²ÑÑÐµ ÐºÐ¾Ð¼Ð¼ÐµÐ½ÑÐ°ÑÐ¸Ð¹ Ð¾ Ð»Ð¸Ð´Ðµ"
            rows={3}
            style={styles.textarea}
          />
          <div style={styles.formActions}>
            <Button text="ÐÐ¾Ð±Ð°Ð²Ð¸ÑÑ" fill disabled={saving || !comment.trim()} onClick={handleAddComment} />
            <span style={styles.hint}>Ctrl + Enter</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadMainInfo;
